import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { fetchGetApplication } from '@iwoca/lapi-client/edge';
import { RootState } from '@iwProvider/configureStore';
import { useQuery } from '@tanstack/react-query';
import { connect } from 'react-redux';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { getTopUpStatus } from '../NewOutstandingView.helpers';
import { InterestOnlyExtensionApprovedAlert } from './Alerts/InterestOnlyExtensionApprovedAlert';
import { NotYetTopUpEligibleAlert } from './Alerts/NotYetTopUpEligibleAlert';
import { TopUpApprovedAlert } from './Alerts/TopUpApprovedAlert';
import { TopUpEligibleAlert } from './Alerts/TopUpEligibleAlert';
import { TopUpPendingAlert } from './Alerts/TopUpPendingAlert';
import { TProductType } from '../../../dashboard/Dashboard.types';

const mapStateToProps = (state: RootState, { productType }: TProps) => {
    return {
        topUpStatus: getTopUpStatus(state, productType)
    };
};

type PropsFromRedux = ReturnType<typeof mapStateToProps>;
type TProps = {
    accountId: string;
    productType: TProductType;
    onAccessFundsClick: () => void;
    redirectToApplyFlexiLoan: () => void;
};

const TopUpStatusAlertBoxes = ({
    accountId,
    topUpStatus,
    onAccessFundsClick,
    redirectToApplyFlexiLoan
}: TProps & PropsFromRedux) => {
    const stateKey = useStateKey();

    const { data: accountsResponse } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: (data) => data.data,
        suspense: true
    });

    const { data: applicationResponse } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey }),
        select: (data) => data.data,
        suspense: true
    });

    const account = accountsResponse!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const application = applicationResponse!;

    switch (topUpStatus) {
        case 'NOT_YET_TOPUP_ELIGIBLE': {
            if (getCountry() === 'UK') return null;

            const amountToRepayBeforeTopUp =
                account.topup_eligibility!.amount_to_repay_before_topup;

            return (
                <NotYetTopUpEligibleAlert
                    amountToRepayBeforeTopUp={amountToRepayBeforeTopUp}
                />
            );
        }
        case 'TOPUP_PENDING': {
            const requestedAmount = application.requested_products![0].amount;

            const requestedCashAmount =
                application.requested_products![0].requested_cash_amount;

            return (
                <TopUpPendingAlert
                    requestedAmount={requestedAmount!}
                    requestedCashAmount={requestedCashAmount!}
                />
            );
        }
        case 'TOPUP_ELIGIBLE':
            return (
                <TopUpEligibleAlert
                    onApplyNowClick={() => {
                        tracking.ampli.topupEligibleBannerCtaClicked({
                            account_id: accountId
                        });
                        redirectToApplyFlexiLoan();
                    }}
                />
            );
        case 'TOPUP_APPROVED':
        case 'PRE_APPROVED': {
            const approvalAmount = account.latest_formal_offer.available_cash!;
            const timeoutDate = account.latest_formal_offer.timeout!;
            const interestRate =
                account.latest_formal_offer.interest_rates![0].rate!;

            //do no show the banner for InterestOnlyPlus for DE
            const isInterestOnlyPlus =
                getDurationInMonths(account.duration!) === 18 &&
                getCountry() === 'DE';
            if (isInterestOnlyPlus) return null;

            return (
                <TopUpApprovedAlert
                    approvalAmount={approvalAmount}
                    timeoutDate={timeoutDate}
                    interestRate={interestRate}
                    onAccessFundsClick={() => {
                        onAccessFundsClick();
                        tracking.ampli.topupApprovedBannerCtaClicked({
                            account_id: accountId
                        });
                    }}
                />
            );
        }
        case 'INTEREST_ONLY_EXTENSION_APPROVED':
        case 'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS':
            const approvalAmount = account.latest_formal_offer.available_cash!;
            const timeoutDate = account.latest_formal_offer.timeout!;
            const interestRate =
                account.latest_formal_offer.interest_rates![0].rate!;

            // There are translations for DE
            if (getCountry() === 'UK') {
                return (
                    <InterestOnlyExtensionApprovedAlert
                        topUpStatus={topUpStatus}
                        onClick={onAccessFundsClick}
                        approvalAmount={approvalAmount}
                        timeoutDate={timeoutDate}
                        interestRate={interestRate}
                    />
                );
            }
            return null;
        default:
            return null;
    }
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(TopUpStatusAlertBoxes);
